import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../../helpers/axiosInstance";
import "./joinorganisation.css";

/**
 * The page an invite link opens.
 *
 * One button. Somebody who has signed in before simply accepts; only a person
 * who has never joined is asked to choose a password, because an invite to a
 * second fund must never be a way to change an existing one.
 *
 * Accepting ends here, on a confirmation — not at the sign-in form. The fund
 * is run from the phone app, so the page offers a deep link into it.
 */
const APP_LINK = process.env.REACT_APP_APP_DEEPLINK || "switchfund://signin";
const PLAY_LINK =
  process.env.REACT_APP_PLAY_STORE_URL ||
  "https://play.google.com/store/apps/details?id=in.switchfund.app";

function JoinOrganisation() {
  const [params] = useSearchParams();
  const token = params.get("token");

  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(null);

  const validate = useCallback(() => {
    if (!token) {
      setError("This link is missing its invite code. Ask for a new one.");
      setChecking(false);
      return;
    }
    axiosInstance
      .post("/api/member/invite/accept?validateOnly=true", { token })
      .then((response) => {
        setInvite(response.data);
        setChecking(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "This invite link is not valid.");
        setChecking(false);
      });
  }, [token]);

  useEffect(() => {
    validate();
  }, [validate]);

  const accept = () => {
    if (invite?.needs_password) {
      if (!password || password.length < 6) {
        setError("Choose a password of at least 6 characters.");
        return;
      }
      if (password !== confirmPassword) {
        setError("The two passwords do not match.");
        return;
      }
    }

    setError(null);
    setSubmitting(true);
    axiosInstance
      .post("/api/member/invite/accept", {
        token,
        ...(invite?.needs_password ? { password } : {}),
      })
      .then((response) => {
        setAccepted(response.data);
        setSubmitting(false);
      })
      .catch((err) => {
        setError(err?.response?.data?.message || "Could not accept the invitation.");
        setSubmitting(false);
      });
  };

  if (checking) {
    return (
      <section className="join-container">
        <div className="join-card">
          <p>Checking your invitation…</p>
        </div>
      </section>
    );
  }

  if (accepted) {
    return (
      <section className="join-container">
        <div className="join-card">
          <div className="join-tick">✓</div>
          <h2>Invitation accepted</h2>
          <p>
            {accepted.message ||
              `You have joined ${accepted.organisation_name || "the fund"}.`}
          </p>
          <p className="join-muted">
            Open the SwitchFund app to see your savings and loans.
          </p>
          <a className="join-button" href={APP_LINK}>
            Open the app
          </a>
          <a className="join-secondary" href={PLAY_LINK}>
            Don’t have it? Get the app
          </a>
        </div>
      </section>
    );
  }

  return (
    <section className="join-container">
      <div className="join-card">
        <h2>Join {invite?.organisation_name || "the fund"}</h2>
        <p className="join-muted">Invitation for {invite?.email}</p>

        {invite?.needs_password ? (
          <>
            <p>Choose a password to finish setting up your account.</p>
            <label htmlFor="join-password">Password</label>
            <input
              id="join-password"
              type="password"
              value={password}
              onChange={({ target }) => setPassword(target.value)}
              autoComplete="new-password"
            />
            <label htmlFor="join-confirm">Confirm password</label>
            <input
              id="join-confirm"
              type="password"
              value={confirmPassword}
              onChange={({ target }) => setConfirmPassword(target.value)}
              autoComplete="new-password"
            />
          </>
        ) : (
          <p>
            You already have a SwitchFund account. Accepting adds this fund to
            it — your password does not change.
          </p>
        )}

        {error ? <p className="join-error">{error}</p> : null}

        <button className="join-button" onClick={accept} disabled={submitting}>
          {submitting ? "Accepting…" : "Accept invitation"}
        </button>
      </div>
    </section>
  );
}

export default JoinOrganisation;
